import { FC } from 'react'
import { Datagrid, FunctionField, List, SearchInput, useTranslate } from 'react-admin'
import { Box, Stack } from '@mui/material'
import BusinessIcon from '@mui/icons-material/Business'
import { CERTIFICATE_ORGANIZATION_WRITE_SCOPE } from '../../config'
import { RestrictedCreateButton } from '../../components'
import { EmptyState, Muted, PageHeader, pageSx } from '../../components/ui'
import { border } from '../../layout/theme/border'
import { themeColor } from '../../layout/theme/color'
import { useSettingsCrumbs } from '../../hooks/useSettingsCrumbs'
import type { OrganizationRecord } from '../../types/apiTypes'

const appPillSx = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 22,
  px: 1,
  borderRadius: border.radius.pill,
  border: `${border.thin} solid ${border.light}`,
  bgcolor: themeColor.slate05,
  color: themeColor.slate80,
  fontSize: 12,
  fontWeight: 500,
  whiteSpace: 'nowrap',
} as const

// Linked apps as "zaken: Gemeente X" pills; falls back to the external id when the name is unknown.
export const linkedApps = (record: OrganizationRecord): JSX.Element => (
  <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.75}>
    {Object.entries(record.apps ?? {})
      .filter(([, externalId]) => !!externalId)
      .map(([key, externalId]) => (
        // `key` comes from Object.entries above, not from user input.
        // eslint-disable-next-line security/detect-object-injection
        <Box key={key} component="span" sx={appPillSx}>{`${key}: ${record.appNames?.[key] || externalId}`}</Box>
      ))}
  </Stack>
)

const OrganizationsList: FC = () => {
  const translate = useTranslate()
  const crumbs = useSettingsCrumbs('organization')
  const createButton = <RestrictedCreateButton scope={CERTIFICATE_ORGANIZATION_WRITE_SCOPE} />

  return (
    <Box sx={pageSx}>
      <PageHeader
        crumbs={crumbs}
        title={translate('resources.organization.title')}
        actions={createButton}
      />
      <List
        actions={false}
        title={translate('resources.organization.title')}
        filters={[<SearchInput key="q" source="q" alwaysOn placeholder={translate('settings.organization.search')} />]}
        sort={{ field: 'name', order: 'ASC' }}
        empty={
          <EmptyState
            icon={<BusinessIcon />}
            title={translate('settings.organization.emptyTitle')}
            text={translate('settings.organization.emptyText')}
            action={createButton}
          />
        }
      >
        <Datagrid rowClick="show" bulkActionButtons={false}>
          <FunctionField<OrganizationRecord>
            source="name"
            label="resources.organization.fields.name"
            className="col-main"
            cellClassName="col-main"
            render={(record) => (
              <>
                <Box sx={{ fontWeight: 600 }}>{record.name}</Box>
                <Muted mono>{record.organizationId}</Muted>
              </>
            )}
          />
          <FunctionField label="resources.organization.fields.apps" render={linkedApps} />
        </Datagrid>
      </List>
    </Box>
  )
}

export default OrganizationsList
